import { BALANCE_CURRENCY_CHANGE, IS_MOBILE_APP_QUERY, MOBILE_APP_BALANCE_ACTIVE_CURRENCY_UPDATED, MOBILE_APP_MESSAGE_MARK_AS_READ } from '@gig/magic-messages';
import { __awaiter, __generator } from 'tslib';
import { MobileAppMessageTypes } from './mobile-app.types.js';

var MobileAppService = /** @class */ (function () {
    function MobileAppService(nativeAppEnabled) {
        var _this = this;
        this.appIdentifier = 'GiGAppZero';
        this.messageResponseTimeout = 20000;
        this.pendingBiometricRequests = [];
        this.pendingTrackingEnabledRequests = [];
        this.pendingCurrencyRequests = [];
        this.pendingSessionStatusRequests = [];
        this.pendingLoginRequests = [];
        var userAgent = window.navigator.userAgent;
        this.isMobileApp =
            !!nativeAppEnabled &&
                userAgent.includes(this.appIdentifier) &&
                typeof window.kcContext === 'undefined';
        window.queryBus.subscribe(IS_MOBILE_APP_QUERY, function () {
            return _this.isMobileApp;
        });
        if (!this.isMobileApp) {
            return;
        }
        window.addEventListener('message', function (event) {
            try {
                if (typeof event.data !== 'string') {
                    return;
                }
                var message = JSON.parse(event.data);
                if (!message.key ||
                    !Object.values(MobileAppMessageTypes).includes(message.key)) {
                    return;
                }
                _this.handleMessage(message);
            }
            catch (error) {
                console.warn("Failed to parse and handle message from Mobile App: " + event.data, error);
            }
        }, true);
        window.eventBus.subscribe(MobileAppMessageTypes.WEBVIEW_LIFECYCLE, function (_a) {
            var payload = _a.payload;
            if (payload === null || payload === void 0 ? void 0 : payload.status) {
                _this.sendMessageToMobileApp({
                    key: MobileAppMessageTypes.WEBVIEW_LIFECYCLE,
                    payload: {
                        status: payload.status,
                    },
                });
            }
        });
        window.eventBus.subscribe(MobileAppMessageTypes.NAVIGATE_TO_FROM_WEBVIEW, function (_a) {
            var payload = _a.payload;
            if (payload === null || payload === void 0 ? void 0 : payload.route) {
                _this.sendMessageToMobileApp({
                    key: MobileAppMessageTypes.NAVIGATE_TO_FROM_WEBVIEW,
                    payload: {
                        route: payload.route,
                    },
                });
            }
        });
        window.eventBus.subscribe(MOBILE_APP_BALANCE_ACTIVE_CURRENCY_UPDATED, function (_a) {
            var payload = _a.payload;
            if (payload) {
                _this.activeCurrencyUpdated(payload);
            }
        });
        window.eventBus.subscribe(MOBILE_APP_MESSAGE_MARK_AS_READ, function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                this.sendMessageToMobileApp({
                    key: MobileAppMessageTypes.MESSAGE_MARK_AS_READ,
                    payload: {},
                });
                return [2 /*return*/];
            });
        }); });
    }
    MobileAppService.prototype.getAppVersion = function () {
        if (!this.isMobileApp) {
            return null;
        }
        // Access the user agent
        var userAgent = window.navigator.userAgent;
        // Regular expression to match the app identifier and capture the version
        var regex = new RegExp(this.appIdentifier + "/([\\w.-]+)");
        var match = userAgent.match(regex);
        // Return the captured version or null if not found
        return match ? match[1] : null;
    };
    MobileAppService.prototype.navigateTo = function (payload) {
        if (!this.isMobileApp) {
            return;
        }
        if (!payload || typeof payload !== 'object' || !payload.route) {
            console.warn('Mobile-app-service: navigateTo called with invalid payload. Message not sent.');
            return;
        }
        if (!window.ReactNativeWebView ||
            typeof window.ReactNativeWebView.postMessage !== 'function') {
            console.warn('Mobile-app-service: Attempting to send message to Mobile app but no postMessage function available');
            return;
        }
        try {
            this.sendMessageToMobileApp({
                key: MobileAppMessageTypes.NAVIGATE_TO_FROM_WEBVIEW,
                payload: {
                    route: payload.route,
                },
            });
        }
        catch (error) {
            console.error('Mobile-app-service: Failed to send message to Mobile app:', error);
        }
    };
    MobileAppService.prototype.activeCurrencyUpdated = function (currency) {
        this.sendMessageToMobileApp({
            key: MobileAppMessageTypes.CURRENCY_CHANGED,
            payload: {
                currency: currency,
            },
        });
    };
    MobileAppService.prototype.biometricLogin = function () {
        return __awaiter(this, void 0, void 0, function () {
            var promise;
            var _this = this;
            return __generator(this, function (_a) {
                promise = new Promise(function (resolve, reject) {
                    // Add a fallback cancel to prevent Login getting stuck
                    var loginFallbackTimeout = setTimeout(function () {
                        reject('Biometric request timed out!');
                    }, _this.messageResponseTimeout);
                    _this.pendingBiometricRequests.push(function (payload) {
                        clearTimeout(loginFallbackTimeout);
                        resolve(payload);
                    });
                });
                this.sendMessageToMobileApp({
                    key: MobileAppMessageTypes.BIOMETRIC_REQUEST,
                    payload: {},
                });
                return [2 /*return*/, promise];
            });
        });
    };
    MobileAppService.prototype.isTrackingEnabled = function () {
        return __awaiter(this, void 0, void 0, function () {
            var promise;
            var _this = this;
            return __generator(this, function (_a) {
                promise = new Promise(function (resolve, reject) {
                    // Add a fallback cancel to prevent request getting stuck
                    var trackingFallbackTimeout = setTimeout(function () {
                        reject('Tracking enabled request timed out!');
                    }, _this.messageResponseTimeout);
                    _this.pendingTrackingEnabledRequests.push(function (payload) {
                        var _a;
                        clearTimeout(trackingFallbackTimeout);
                        resolve((_a = payload === null || payload === void 0 ? void 0 : payload.enabled) !== null && _a !== void 0 ? _a : false);
                    });
                });
                this.sendMessageToMobileApp({
                    key: MobileAppMessageTypes.TRACKING_ENABLED_REQUEST,
                    payload: {},
                });
                return [2 /*return*/, promise];
            });
        });
    };
    MobileAppService.prototype.getCurrentCurrency = function () {
        return __awaiter(this, void 0, void 0, function () {
            var promise;
            var _this = this;
            return __generator(this, function (_a) {
                if (!this.isMobileApp) {
                    return [2 /*return*/, Promise.reject('getCurrentCurrency not running in mobile app')];
                }
                promise = new Promise(function (resolve, reject) {
                    // Add a fallback cancel to prevent request getting stuck
                    var currentCurrencyFallbackTimeout = setTimeout(function () {
                        reject('Current currency request timed out!');
                    }, _this.messageResponseTimeout);
                    _this.pendingCurrencyRequests.push(function (payload) {
                        clearTimeout(currentCurrencyFallbackTimeout);
                        resolve(payload.currency);
                    });
                });
                this.sendMessageToMobileApp({
                    key: MobileAppMessageTypes.CURRENCY_CHANGED_REQUEST,
                    payload: {},
                });
                return [2 /*return*/, promise];
            });
        });
    };
    MobileAppService.prototype.checkSessionStatus = function () {
        return __awaiter(this, void 0, void 0, function () {
            var promise;
            var _this = this;
            return __generator(this, function (_a) {
                promise = new Promise(function (resolve, reject) {
                    // Add a fallback cancel to prevent request getting stuck
                    var sessionStatusFallbackTimeout = setTimeout(function () {
                        reject('Session status request timed out!');
                    }, _this.messageResponseTimeout);
                    _this.pendingSessionStatusRequests.push(function (payload) {
                        clearTimeout(sessionStatusFallbackTimeout);
                        resolve(payload);
                    });
                });
                this.sendMessageToMobileApp({
                    key: MobileAppMessageTypes.SESSION_STATUS_REQUEST,
                    payload: {},
                });
                return [2 /*return*/, promise];
            });
        });
    };
    MobileAppService.prototype.login = function (params) {
        return __awaiter(this, void 0, void 0, function () {
            var promise;
            var _this = this;
            return __generator(this, function (_a) {
                promise = new Promise(function (resolve, reject) {
                    var loginFallbackTimeout = setTimeout(function () {
                        reject('Login request timed out!');
                    }, _this.messageResponseTimeout);
                    _this.pendingLoginRequests.push(function (payload) {
                        clearTimeout(loginFallbackTimeout);
                        resolve(payload.result);
                    });
                });
                this.sendMessageToMobileApp({
                    key: MobileAppMessageTypes.LOGIN_REQUEST,
                    payload: { params: params },
                });
                return [2 /*return*/, promise];
            });
        });
    };
    MobileAppService.prototype.logout = function () {
        this.sendMessageToMobileApp({
            key: MobileAppMessageTypes.LOGOUT_REQUEST,
            payload: null,
        });
    };
    MobileAppService.prototype.onSessionMessage = function (callback) {
        this.sessionMessageHandler = callback;
    };
    MobileAppService.prototype.onSocketEvent = function (callback) {
        var _this = this;
        this.socketEventHandler = callback;
        return function () { return (_this.socketEventHandler = undefined); };
    };
    MobileAppService.prototype.sendMessageToMobileApp = function (message) {
        var _a, _b;
        if (!this.isMobileApp) {
            return;
        }
        if (typeof ((_a = window.ReactNativeWebView) === null || _a === void 0 ? void 0 : _a.postMessage) !==
            'function') {
            console.warn('Attempting to send message to app but no postMessage function available');
            return;
        }
        try {
            message.payload = (_b = message.payload) !== null && _b !== void 0 ? _b : {};
            var data = JSON.stringify(message);
            window.ReactNativeWebView.postMessage(data);
        }
        catch (error) {
            console.error('Error sending message to mobile app', error);
        }
    };
    MobileAppService.prototype.isNavigateToMessageType = function (payload) {
        return ((payload && typeof payload === 'object' && 'route' in payload) ||
            false);
    };
    MobileAppService.prototype.isBiometricResponseType = function (payload) {
        var valid = (payload && typeof payload === 'object' && 'success' in payload) ||
            false;
        if (valid && payload.success) {
            valid = typeof payload.session === 'object';
        }
        return valid;
    };
    MobileAppService.prototype.isTrackingEnabledResponseType = function (payload) {
        return ((payload &&
            typeof payload === 'object' &&
            'enabled' in payload &&
            typeof payload.enabled === 'boolean') ||
            false);
    };
    MobileAppService.prototype.isCurrencyChangedMessageType = function (payload) {
        return ((payload &&
            typeof payload === 'object' &&
            'currency' in payload &&
            typeof payload.currency === 'string') ||
            false);
    };
    MobileAppService.prototype.isSessionMessageType = function (message) {
        return (message.key === MobileAppMessageTypes.SESSION_CREATED ||
            message.key === MobileAppMessageTypes.SESSION_UPDATED ||
            message.key === MobileAppMessageTypes.SESSION_DESTROYED);
    };
    MobileAppService.prototype.isSessionStatusResponseType = function (message) {
        return (message.key === MobileAppMessageTypes.SESSION_STATUS_RESPONSE &&
            typeof message.payload.status !==
                'undefined');
    };
    MobileAppService.prototype.isLoginResponseType = function (message) {
        return (message.key === MobileAppMessageTypes.LOGIN_RESPONSE &&
            typeof message.payload.result ===
                'boolean');
    };
    MobileAppService.prototype.isSocketEventType = function (message) {
        return (message.key === MobileAppMessageTypes.SOCKET_EVENT &&
            typeof message.payload.type ===
                'string');
    };
    MobileAppService.prototype.handleMessage = function (message) {
        var _a, _b;
        if (this.isNavigateToMessageType(message.payload)) {
            window.eventBus.publish(MobileAppMessageTypes.NAVIGATE_TO_FROM_MOBILE_APP, message);
        }
        else if (this.isBiometricResponseType(message.payload)) {
            this.handleBiometricResponse(message.payload);
        }
        else if (this.isTrackingEnabledResponseType(message.payload)) {
            this.handleTrackingEnabledResponse(message.payload);
        }
        else if (this.isCurrencyChangedMessageType(message.payload)) {
            this.handleCurrencyChanged(message.payload);
        }
        else if (this.isSessionStatusResponseType(message)) {
            this.handleSessionStatusResponse(message.payload);
        }
        else if (this.isLoginResponseType(message)) {
            this.handleLoginResponse(message.payload);
        }
        else if (this.isSessionMessageType(message)) {
            (_a = this.sessionMessageHandler) === null || _a === void 0 ? void 0 : _a.call(this, message);
        }
        else if (this.isSocketEventType(message)) {
            (_b = this.socketEventHandler) === null || _b === void 0 ? void 0 : _b.call(this, message.payload);
        }
        else {
            // eslint-disable-next-line no-console
            console.log('MOBILE_APP_SERVICE_HANDLE_MESSAGE:', message);
        }
    };
    MobileAppService.prototype.handleBiometricResponse = function (payload) {
        while (this.pendingBiometricRequests.length > 0) {
            var bioCallback = this.pendingBiometricRequests.pop();
            bioCallback(payload);
        }
    };
    MobileAppService.prototype.handleTrackingEnabledResponse = function (payload) {
        while (this.pendingTrackingEnabledRequests.length > 0) {
            var trackingCallback = this.pendingTrackingEnabledRequests.pop();
            trackingCallback(payload);
        }
    };
    MobileAppService.prototype.handleCurrencyChanged = function (payload) {
        while (this.pendingCurrencyRequests.length > 0) {
            var currencyCallback = this.pendingCurrencyRequests.pop();
            currencyCallback(payload);
        }
        window.commandBus.publish(BALANCE_CURRENCY_CHANGE, payload.currency);
    };
    MobileAppService.prototype.handleSessionStatusResponse = function (payload) {
        while (this.pendingSessionStatusRequests.length > 0) {
            var sessionCallback = this.pendingSessionStatusRequests.pop();
            sessionCallback(payload);
        }
    };
    MobileAppService.prototype.handleLoginResponse = function (payload) {
        while (this.pendingLoginRequests.length > 0) {
            var sessionCallback = this.pendingLoginRequests.pop();
            sessionCallback(payload);
        }
    };
    return MobileAppService;
}());

export { MobileAppService };
